import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import FieldGroupWrapper from './wrappers/field-group-wrapper';
import { ISelectOptions } from './types';

type TimeInputProps = {
	label?: string;
	placeholder?: string;
	options: ISelectOptions[];
	value: string;
	onChange?: (e: SelectChangeEvent) => void;
};

interface IProps {
	id: string;
	legend: string;
	description?: string;
	hasError?: boolean;
	errorText?: string;
	hourInputProps: TimeInputProps;
	minuteInputProps: TimeInputProps;
}

interface ILabelProps {
	visible: boolean;
	htmlFor: string;
	text: string;
}

const TimeInputLabel = ({ htmlFor, visible, text }: ILabelProps) => (
	<InputLabel
		shrink={false}
		htmlFor={htmlFor}
		sx={{
			visibility: visible ? 'hidden' : 'visible',
			'&.Mui-focused': {
				color: 'common.black',
			},
		}}
	>
		{text}
	</InputLabel>
);

/**
 * TimeFieldGroup component that renders hour and minute dropdown selectors wrapped in a fieldset for accessibility.
 *
 * Best practices:
 * - Treat hour and minute fields as a single time unit
 * - Use a single form value structure: { hours: string, minutes: string }
 * - Apply validation to both fields collectively
 *
 * @param props - Component properties
 * @param props.id - Unique identifier for accessibility (required)
 * @param props.legend - Fieldset legend text - styled like an individual input label
 * @param props.description - Optional description text
 * @param props.hasError - Error state flag - will show error styles and messages for both fields via the `FieldGroupWrapper`
 * @param props.errorText - Error message text - will be displayed below the fields when hasError is true
 * @param props.hourInputProps - Configuration for hours dropdown
 * @param props.minuteInputProps - Configuration for minutes dropdown
 * @param props.hourInputProps.label - Label for hours dropdown
 * @param props.hourInputProps.placeholder - Placeholder for hours dropdown - shows as greyed out text on click
 * @param props.hourInputProps.options - Options for hours dropdown - typically `{ label: string, value: string }[]`
 * @param props.hourInputProps.value - Selected value for hours dropdown
 * @param props.hourInputProps.onChange - Change event handler for hours dropdown
 * @param props.minuteInputProps.label - Label for minutes dropdown
 * @param props.minuteInputProps.placeholder - Placeholder for minutes dropdown - shows as greyed out text on click
 * @param props.minuteInputProps.options - Options for minutes dropdown - typically `{ label: string, value: string }[]`
 * @param props.minuteInputProps.value - Selected value for minutes dropdown
 * @param props.minuteInputProps.onChange - Change event handler for minutes dropdown
 */
const TimeFieldGroup = ({ id, legend, description, hourInputProps, minuteInputProps, hasError, errorText }: IProps) => {
	return (
		<FieldGroupWrapper id={id} legend={legend} description={description} hasError={!!hasError} helperText={errorText}>
			<Box sx={{ display: 'flex', gap: 2 }}>
				{/* HOURS */}
				<FormControl fullWidth error={hasError}>
					<TimeInputLabel
						htmlFor={`${id}-hours`}
						visible={!!hourInputProps.value}
						text={hourInputProps.label ?? 'Hours'}
					/>
					<Select
						inputProps={{ id: `${id}-hours` }}
						value={hourInputProps.value}
						color='secondary'
						onChange={hourInputProps.onChange}
					>
						<MenuItem disabled value=''>
							<em>{hourInputProps.placeholder ?? 'Select hours'}</em>
						</MenuItem>
						{hourInputProps.options.map((h) => (
							<MenuItem key={h.label} value={h.value}>
								{h.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				{/* MINUTES */}
				<FormControl error={hasError} fullWidth>
					<TimeInputLabel
						htmlFor={`${id}-minutes`}
						visible={!!minuteInputProps.value}
						text={minuteInputProps.label ?? 'Minutes'}
					/>
					<Select
						inputProps={{ id: `${id}-minutes` }}
						color='secondary'
						value={minuteInputProps.value}
						onChange={minuteInputProps.onChange}
					>
						<MenuItem disabled value=''>
							<em>{minuteInputProps.placeholder ?? 'Select minutes'}</em>
						</MenuItem>
						{minuteInputProps.options.map((m) => (
							<MenuItem key={m.label} value={m.value}>
								{m.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>
		</FieldGroupWrapper>
	);
};

export default TimeFieldGroup;
